import {
  forwardRef,
  useId,
  type SelectHTMLAttributes,
} from "react";
import { cx } from "../utils/cx";

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  disabled = false,
  className,
}: SwitchProps) {
  const descriptionId = useId();
  return (
    <label className={cx("ui-switch-field", className)}>
      <span className="ui-switch-field__copy">
        <strong>{label}</strong>
        {description ? <small id={descriptionId}>{description}</small> : null}
      </span>
      <span className="ui-switch">
        <input
          type="checkbox"
          role="switch"
          aria-label={label}
          checked={checked}
          disabled={disabled}
          aria-describedby={description ? descriptionId : undefined}
          onChange={(event) => onCheckedChange(event.currentTarget.checked)}
        />
        <span aria-hidden="true" />
      </span>
    </label>
  );
}

export interface SelectProps
  extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  description?: string;
  error?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    { label, description, error, className, id: providedId, ...props },
    ref,
  ) {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const descriptionId = `${id}-description`;
    const errorId = `${id}-error`;
    return (
      <label className={cx("ui-select-field", className)} htmlFor={id}>
        <span className="ui-field__label">{label}</span>
        {description ? (
          <span className="ui-field__description" id={descriptionId}>
            {description}
          </span>
        ) : null}
        <select
          {...props}
          ref={ref}
          id={id}
          className="ui-input ui-select"
          aria-invalid={error ? true : undefined}
          aria-describedby={[
            description ? descriptionId : "",
            error ? errorId : "",
          ].filter(Boolean).join(" ") || undefined}
        />
        {error ? (
          <span className="ui-field__error" id={errorId} role="alert">
            {error}
          </span>
        ) : null}
      </label>
    );
  },
);
